/**
 * Leitura do estado de rastreabilidade do trader (DSA Art. 30) e da informação pública do Art. 31.
 *
 * A camada de dados mantém a regra fora das telas: a UI mostra um estado, não decide o que conta
 * como verificado. A RLS continua sendo a fronteira de segurança — este módulo apenas valida a
 * forma da resposta. Nenhuma função aqui escreve: a decisão de "está verificado" é do servidor,
 * pela Edge Function `trader-verify`.
 */

export const TRADER_VERIFICATION_STATUSES = [
  'not_started',
  'pending',
  'verified',
  'failed',
  'expired',
] as const;
export type TraderVerificationStatus = (typeof TRADER_VERIFICATION_STATUSES)[number];

/** O que o dono do negócio pode ler sobre a própria verificação. */
export interface TraderVerification {
  business_id: string;
  status: TraderVerificationStatus;
  verified_at: string | null;
  failure_reason: string | null;
  last_checked_at: string | null;
}

/** O que qualquer consumidor pode ler sobre um trader verificado (Art. 31). */
export interface PublicTraderInfo {
  business_id: string;
  legal_name: string | null;
  address_line: string | null;
  city: string | null;
  postal_code: string | null;
  country: string;
  contact_email: string | null;
  trade_register_ref: string | null;
  nif: string | null;
  verified_at: string | null;
}

export interface TraderClient {
  from(table: string): {
    // O client do Supabase é injetado para manter o módulo testável sem rede.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    select(columns: string): any;
  };
}

export class TraderDataError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'TraderDataError';
  }
}

const VERIFICATION_COLUMNS = [
  'business_id',
  'status',
  'verified_at',
  'failure_reason',
  'last_checked_at',
].join(', ');

const PUBLIC_COLUMNS = [
  'business_id',
  'legal_name',
  'address_line',
  'city',
  'postal_code',
  'country',
  'contact_email',
  'trade_register_ref',
  'nif',
  'verified_at',
].join(', ');

const STATUS_SET = new Set<string>(TRADER_VERIFICATION_STATUSES);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function assertBusinessId(businessId: string): void {
  if (!UUID_RE.test(businessId)) throw new TraderDataError('business_id_invalido');
}

function assertNullableString(value: unknown, campo: string): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') throw new TraderDataError(`${campo}_invalido`);
  return value;
}

function parseVerification(raw: unknown): TraderVerification {
  if (typeof raw !== 'object' || raw === null) throw new TraderDataError('trader_verification_invalida');

  const value = raw as Record<string, unknown>;
  if (typeof value.business_id !== 'string' || !UUID_RE.test(value.business_id)) {
    throw new TraderDataError('trader_verification_invalida');
  }
  if (!STATUS_SET.has(String(value.status))) {
    throw new TraderDataError('trader_verification_invalida');
  }

  return {
    business_id: value.business_id,
    status: value.status as TraderVerificationStatus,
    verified_at: assertNullableString(value.verified_at, 'verified_at'),
    failure_reason: assertNullableString(value.failure_reason, 'failure_reason'),
    last_checked_at: assertNullableString(value.last_checked_at, 'last_checked_at'),
  };
}

function parsePublicInfo(raw: unknown): PublicTraderInfo {
  if (typeof raw !== 'object' || raw === null) throw new TraderDataError('public_trader_info_invalida');

  const value = raw as Record<string, unknown>;
  if (typeof value.business_id !== 'string' || !UUID_RE.test(value.business_id)) {
    throw new TraderDataError('public_trader_info_invalida');
  }
  if (typeof value.country !== 'string' || value.country === '') {
    throw new TraderDataError('public_trader_info_invalida');
  }

  return {
    business_id: value.business_id,
    legal_name: assertNullableString(value.legal_name, 'legal_name'),
    address_line: assertNullableString(value.address_line, 'address_line'),
    city: assertNullableString(value.city, 'city'),
    postal_code: assertNullableString(value.postal_code, 'postal_code'),
    country: value.country,
    contact_email: assertNullableString(value.contact_email, 'contact_email'),
    trade_register_ref: assertNullableString(value.trade_register_ref, 'trade_register_ref'),
    nif: assertNullableString(value.nif, 'nif'),
    verified_at: assertNullableString(value.verified_at, 'verified_at'),
  };
}

/**
 * Estado da verificação do próprio negócio. Devolve `null` quando ainda não há linha — um negócio
 * recém-criado não tem dossiê, o que é diferente de ter um dossiê reprovado.
 */
export async function getTraderVerification(
  client: TraderClient,
  businessId: string,
): Promise<TraderVerification | null> {
  assertBusinessId(businessId);

  const { data, error } = await client
    .from('trader_verifications')
    .select(VERIFICATION_COLUMNS)
    .eq('business_id', businessId)
    .maybeSingle();

  if (error) throw new TraderDataError('falha_ao_ler_verificacao', { cause: error });
  if (!data) return null;

  return parseVerification(data);
}

/**
 * Informação pública do trader (DSA Art. 31). Devolve `null` quando o negócio não está `active`
 * com verificação `verified` — a VIEW já aplica esse recorte, e a ausência é a resposta correta.
 */
export async function getPublicTraderInfo(
  client: TraderClient,
  businessId: string,
): Promise<PublicTraderInfo | null> {
  assertBusinessId(businessId);

  const { data, error } = await client
    .from('public_trader_info')
    .select(PUBLIC_COLUMNS)
    .eq('business_id', businessId)
    .maybeSingle();

  if (error) throw new TraderDataError('falha_ao_ler_info_publica', { cause: error });
  if (!data) return null;

  return parsePublicInfo(data);
}

/**
 * Um negócio só pode ofertar com verificação `verified` (DSA Art. 30). Existe como função para que
 * nenhuma tela reimplemente a comparação — e para que a regra tenha um só lugar se mudar.
 */
export function podeOfertar(verification: TraderVerification | null): boolean {
  return verification?.status === 'verified';
}
